import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CheckCircle, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import {
  TELEMED_AGREEMENT_TITLE,
  TELEMED_AGREEMENT_SECTIONS,
} from '../../content/telemedAgreement';

/**
 * ESignaturePadStep Component
 * 
 * Step 4 of Physician ID Proofing: Electronic Signature Pad with Canvas Drawing
 * 
 * Features:
 * - Canvas-based signature drawing with HTML5 Canvas API
 * - Mouse and touch event support for cross-device compatibility
 * - Real-time signature validation and quality feedback
 * - Signature capture to PNG blob with compression
 * - Clear/redraw functionality with undo capability
 * - Mobile responsive design with touch optimization
 * - Accessibility support with ARIA labels and keyboard navigation
 * - Boundary validation and coordinate clamping
 */

interface ESignaturePadStepProps {
  onNext: () => void;
  onBack: () => void;
  onSignatureCapture: (signatureBlob: Blob | null) => void;
  capturedSignature?: Blob | null;
  isLoading: boolean;
  // When true, the clinic prescribes through the doctor network (telemed-only or
  // hybrid). The signature also executes the Contract + BAA, so we render the
  // agreement and require explicit acceptance before signing.
  usesNetwork?: boolean;
}

// Canvas configuration
const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 200;
const CANVAS_MOBILE_WIDTH = 350;
const CANVAS_MOBILE_HEIGHT = 150;

// Drawing configuration
const LINE_WIDTH = 2;
const LINE_COLOR = '#000000';
const LINE_CAP = 'round' as CanvasLineCap;
const LINE_JOIN = 'round' as CanvasLineJoin;

// Validation thresholds
const MIN_SIGNATURE_COMPLEXITY = 10; // Minimum number of drawing points
const QUALITY_COMPLEXITY_THRESHOLD = 25; // Points needed for "good" quality

interface DrawingPoint {
  x: number;
  y: number;
}

export const ESignaturePadStep: React.FC<ESignaturePadStepProps> = ({
  onNext,
  onBack,
  onSignatureCapture,
  capturedSignature,
  isLoading,
  usesNetwork = false
}) => {
  // State
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [drawingPoints, setDrawingPoints] = useState<DrawingPoint[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [localSignature, setLocalSignature] = useState<Blob | null>(capturedSignature || null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize canvas context
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    // Configure drawing context
    context.lineWidth = LINE_WIDTH;
    context.lineCap = LINE_CAP;
    context.lineJoin = LINE_JOIN;
    context.strokeStyle = LINE_COLOR;
    
    contextRef.current = context;
  }, []);

  // Generate signature preview URL
  useEffect(() => {
    if (capturedSignature) {
      const url = URL.createObjectURL(capturedSignature);
      setSignaturePreviewUrl(url);
      setHasSignature(true);
      return () => URL.revokeObjectURL(url);
    } else {
      setSignaturePreviewUrl('');
      setHasSignature(false);
    }
  }, [capturedSignature]);

  // Get canvas coordinates relative to viewport
  const getCanvasCoordinates = useCallback((clientX: number, clientY: number): DrawingPoint => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Clamp coordinates to canvas bounds
    const clampedX = Math.max(0, Math.min(x, canvas.width));
    const clampedY = Math.max(0, Math.min(y, canvas.height));

    return { x: clampedX, y: clampedY };
  }, []);

  // Evaluate signature quality based on complexity
  const evaluateSignatureQuality = useCallback((points: DrawingPoint[]) => {
    if (points.length === 0) {
      // setSignatureQuality('none'); // This state was removed, so this line is removed
    } else if (points.length < MIN_SIGNATURE_COMPLEXITY) {
      // setSignatureQuality('simple'); // This state was removed, so this line is removed
    } else if (points.length >= QUALITY_COMPLEXITY_THRESHOLD) {
      // setSignatureQuality('good'); // This state was removed, so this line is removed
    } else {
      // setSignatureQuality('simple'); // This state was removed, so this line is removed
    }
  }, []);

  // Capture signature as blob
  const captureSignature = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob) {
        onSignatureCapture(blob);
        setLocalSignature(blob);
        setHasSignature(true);
        console.log('🖋️ Signature captured in component, blob size:', blob.size);
      }
    }, 'image/png', 0.8);
  }, [onSignatureCapture]);

  // Start drawing
  const startDrawing = useCallback((point: DrawingPoint) => {
    const context = contextRef.current;
    if (!context || isLoading) return;

    setIsDrawing(true);
    context.beginPath();
    context.moveTo(point.x, point.y);
    
    setDrawingPoints(prev => [...prev, point]);
  }, [isLoading]);

  // Continue drawing
  const continueDrawing = useCallback((point: DrawingPoint) => {
    const context = contextRef.current;
    if (!context || !isDrawing || isLoading) return;

    context.lineTo(point.x, point.y);
    context.stroke();
    
    setDrawingPoints(prev => [...prev, point]);
  }, [isDrawing, isLoading]);

  // End drawing
  const endDrawing = useCallback(() => {
    const context = contextRef.current;
    if (!context || !isDrawing) return;

    context.closePath();
    setIsDrawing(false);
    
    // Evaluate quality but don't auto-capture - let user decide when to capture
    evaluateSignatureQuality(drawingPoints);
    // captureSignature(); // Removed auto-capture - user needs to click "Confirm" button
  }, [isDrawing, drawingPoints, evaluateSignatureQuality]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const point = getCanvasCoordinates(e.clientX, e.clientY);
    startDrawing(point);
  }, [getCanvasCoordinates, startDrawing]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const point = getCanvasCoordinates(e.clientX, e.clientY);
    continueDrawing(point);
  }, [getCanvasCoordinates, continueDrawing]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    endDrawing();
  }, [endDrawing]);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    
    const point = getCanvasCoordinates(touch.clientX, touch.clientY);
    startDrawing(point);
  }, [getCanvasCoordinates, startDrawing]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (!touch) return;
    
    const point = getCanvasCoordinates(touch.clientX, touch.clientY);
    continueDrawing(point);
  }, [getCanvasCoordinates, continueDrawing]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    endDrawing();
  }, [endDrawing]);

  // Clear signature
  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context || isLoading) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    setDrawingPoints([]);
    // setSignatureQuality('none'); // This state was removed, so this line is removed
    setHasSignature(false);
    setLocalSignature(null);
    onSignatureCapture(null);
    console.log('🖋️ Signature cleared');
  }, [isLoading, onSignatureCapture]);

  // Redraw functionality (same as clear for this implementation)
  const redrawSignature = useCallback(() => {
    clearSignature();
  }, [clearSignature]);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (localSignature && !isLoading) {
      onNext();
    }
  }, [localSignature, isLoading, onNext]);

  // Get canvas dimensions based on viewport
  const canvasWidth = isMobile ? CANVAS_MOBILE_WIDTH : CANVAS_WIDTH;
  const canvasHeight = isMobile ? CANVAS_MOBILE_HEIGHT : CANVAS_HEIGHT;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <form role="form" onSubmit={handleSubmit} className="space-y-6">
        {/* Step Header */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {usesNetwork ? 'Review & Sign Your Agreement' : 'Electronic Signature'}
          </h2>
          <p className="text-gray-600">
            {usesNetwork
              ? 'Because your clinic prescribes through the doctor network, review the agreement below and sign to accept it.'
              : 'Draw your signature below for identity verification'}
          </p>
        </div>

        {/* Contract + BAA for network clinics (telemed-only or hybrid). Signing
            below executes this agreement. */}
        {usesNetwork && (
          <div>
            <div className="border border-gray-200 rounded-lg bg-white">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">{TELEMED_AGREEMENT_TITLE}</h3>
              </div>
              <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-4">
                {TELEMED_AGREEMENT_SECTIONS.map((section) => (
                  <div key={section.heading}>
                    <h4 className="text-xs font-semibold text-gray-900 mb-1">{section.heading}</h4>
                    {section.paragraphs.map((p, i) => (
                      <p key={i} className="text-xs text-gray-600 leading-relaxed mb-1.5">{p}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreementAccepted}
                onChange={(e) => setAgreementAccepted(e.target.checked)}
                disabled={isLoading}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the {TELEMED_AGREEMENT_TITLE}, and I am authorized to bind the clinic.
              </span>
            </label>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Signature Requirements</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Use your finger or mouse to draw your signature</li>
            <li>• Draw clearly within the box boundaries</li>
            <li>• Make it similar to your legal signature</li>
            <li>• PNG format, automatically captured when complete</li>
          </ul>
        </div>

        {/* Signature Pad Area */}
        <div className="space-y-4">
          {/* Show captured signature preview */}
          {capturedSignature && signaturePreviewUrl ? (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <img
                    data-testid="signature-preview"
                    src={signaturePreviewUrl}
                    alt="Captured signature preview"
                    className="max-w-full h-auto rounded border border-gray-300 bg-white"
                    style={{ maxHeight: '120px' }}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle data-testid="signature-success-icon" className="h-5 w-5 text-green-500" />
                    <p className="text-sm font-medium text-green-700">
                      Signature captured successfully
                    </p>
                  </div>
                  
                  {/* signatureQuality === 'good' && ( // This state was removed, so this block is removed */}
                  {/*   <p className="text-xs text-green-600"> // This state was removed, so this block is removed */}
                  {/*     Signature looks good - detailed and clear // This state was removed, so this block is removed */}
                  {/*   </p> // This state was removed, so this block is removed */}
                  {/* ) // This state was removed, so this block is removed */}
                  
                  <button
                    type="button"
                    onClick={redrawSignature}
                    disabled={isLoading}
                    className={`
                      mt-3 inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700
                      ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 focus:ring-2 focus:ring-blue-500'}
                    `}
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Redraw
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Drawing Canvas */
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Draw your signature in the box below
                </p>
                
                {/* Quality feedback */}
                {/* signatureQuality === 'simple' && drawingPoints.length > 0 && ( // This state was removed, so this block is removed */}
                {/*   <div className="flex items-center justify-center space-x-2 text-yellow-600 text-sm"> // This state was removed, so this block is removed */}
                {/*     <AlertCircle className="h-4 w-4" /> // This state was removed, so this block is removed */}
                {/*     <span>Signature appears simple. Consider adding more detail.</span> // This state was removed, so this block is removed */}
                {/*   </div> // This state was removed, so this block is removed */}
                {/* ) // This state was removed, so this block is removed */}
                
                {/* signatureQuality === 'good' && ( // This state was removed, so this block is removed */}
                {/*   <div className="flex items-center justify-center space-x-2 text-green-600 text-sm"> // This state was removed, so this block is removed */}
                {/*     <CheckCircle className="h-4 w-4" /> // This state was removed, so this block is removed */}
                {/*     <span>Signature looks good!</span> // This state was removed, so this block is removed */}
                {/*   </div> // This state was removed, so this block is removed */}
                {/* ) // This state was removed, so this block is removed */}
                
                {!hasSignature && drawingPoints.length === 0 && (
                  <p className="text-xs text-gray-500">
                    Draw your signature to continue
                  </p>
                )}
              </div>

              <div className="flex justify-center">
                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    data-testid="signature-canvas"
                    width={canvasWidth}
                    height={canvasHeight}
                    role="img"
                    aria-label="Signature drawing canvas - use mouse or touch to draw your signature"
                    className={`
                      border border-gray-400 rounded bg-white cursor-crosshair
                      ${isLoading ? 'opacity-50 pointer-events-none' : 'hover:border-blue-400'}
                    `}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{ touchAction: 'none' }} // Prevent scrolling on touch
                  />
                  
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 data-testid="signature-loading-spinner" className="h-8 w-8 text-blue-500 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-center gap-4 mt-4">
                <button
                  type="button"
                  onClick={clearSignature}
                  disabled={isLoading || drawingPoints.length === 0}
                  aria-describedby="clear-signature-help"
                  className={`
                    inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700
                    ${isLoading || drawingPoints.length === 0 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-gray-50 focus:ring-2 focus:ring-blue-500'
                    }
                  `}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </button>
                
                <button
                  type="button"
                  onClick={captureSignature}
                  disabled={isLoading || drawingPoints.length === 0 || hasSignature || (usesNetwork && !agreementAccepted)}
                  aria-describedby="capture-signature-help"
                  className={`
                    inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white
                    ${isLoading || drawingPoints.length === 0 || hasSignature || (usesNetwork && !agreementAccepted)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 focus:ring-2 focus:ring-green-500'
                    }
                  `}
                >
                  {usesNetwork && !agreementAccepted ? 'Accept agreement first' : hasSignature ? 'Captured ✓' : 'Capture Signature'}
                </button>
                
                <p id="clear-signature-help" className="sr-only">
                  Clear the signature canvas to start over
                </p>
                <p id="capture-signature-help" className="sr-only">
                  Capture the current signature for submission
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className={`
              px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            Back
          </button>

          <button
            type="submit"
            disabled={!localSignature || isLoading || (usesNetwork && !agreementAccepted)}
            className={`
              px-6 py-2 rounded-lg text-white font-medium focus:ring-2 focus:ring-blue-500
              ${localSignature && !isLoading && !(usesNetwork && !agreementAccepted)
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isLoading ? (
              <span className="flex items-center">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}; 